import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackClose } from '@/hooks/useBackClose';
import { PQShield } from '@/components/pq/PQMark';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/dashboard/PageHeader';
import FindingsReview from '@/components/pqv/FindingsReview';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AlertCircle,
  ArrowLeft,
  Camera,
  CheckCircle2,
  Loader2,
  MapPin,
  Plus,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  fetchWalkthrough,
  listAreas,
  addArea,
  uploadAreaPhoto,
  listAreaPhotos,
  signedUrlsForPaths,
  addAreaNote,
  updateAreaNoteBody,
  listAreaNotes,
  completeWalkthrough,
  deleteArea,
  deleteAreaPhoto,
  deleteAreaNote,
} from '@/lib/pqvData';

/**
 * PQV Capture page — area-first photo + note walkthrough.
 *
 * Route: /dashboard/pq-vision/walkthrough/:id
 *
 * The walkthrough row is created by the Estimates page "PQ Vision GO"
 * action; this page only reads it (then writes areas/photos/notes against it).
 *
 * UX (v2):
 *   - Row of area chips at the top. Tap to select. "+ Add area" inserts a
 *     new pqv_area row immediately (no manual save).
 *   - For the selected area: a camera/file input (auto-uploads on pick), a
 *     thumbnail grid of all uploaded photos, and a single typed-notes box
 *     that auto-saves on debounce (~1s of inactivity) and on blur. No
 *     manual "Save" button anywhere.
 *   - Every area / photo / note has a "SHIT" delete button (confirm prompt
 *     before commit). Photo delete also removes the file from storage.
 *   - "Done" marks the walkthrough complete (status='complete') and returns
 *     to the Estimates module. Does NOT lock the walkthrough — the user can
 *     reopen it via "PQ RE-View" and keep editing.
 *
 * No AI, no analysis — pure capture.
 */
export default function PQVCapturePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: walkthroughId } = useParams();
  const fileInputRef = useRef(null);

  const [walkthrough, setWalkthrough] = useState(null);
  const [areas, setAreas] = useState([]);
  const [activeAreaId, setActiveAreaId] = useState(null);
  const [photos, setPhotos] = useState([]); // photos for active area
  const [photoUrls, setPhotoUrls] = useState({}); // storage_path → signed url
  const [notes, setNotes] = useState([]); // notes for active area
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [addingArea, setAddingArea] = useState(false);
  const [newAreaName, setNewAreaName] = useState('');
  // Autosave note state. We persist a single "primary" note row per area —
  // the first one inserted — and PATCH it on subsequent edits. Switching
  // areas hydrates this draft from whatever note row exists.
  const [noteDraft, setNoteDraft] = useState('');
  const [primaryNoteId, setPrimaryNoteId] = useState(null);
  const [noteSavingState, setNoteSavingState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const noteDebounceRef = useRef(null);
  const noteLastSavedRef = useRef(''); // last body successfully persisted
  const [completing, setCompleting] = useState(false);

  // Delete confirmation modal — used by the SHIT button on areas, photos,
  // and notes. `target` is { kind: 'area'|'photo'|'note', row: object }.
  const [confirmDelete, setConfirmDelete] = useState({ open: false, target: null, busy: false });

  // Lightbox — when set, renders a full-screen overlay of the photo. We hold
  // the full photo row so we can show metadata if needed later; the rendered
  // image uses the same signed URL the thumbnail already minted.
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  // Phone Back closes the photo instead of leaving the walkthrough
  // (dogfood 2026-09-24: back from a photo dumped John on the estimates list).
  useBackClose(!!lightboxPhoto, () => setLightboxPhoto(null));

  /* -------------------------------------------------------------------------
   * Initial load — walkthrough metadata + its areas.
   * ---------------------------------------------------------------------- */
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [w, a] = await Promise.all([
        fetchWalkthrough(walkthroughId),
        listAreas(walkthroughId),
      ]);
      setWalkthrough(w);
      setAreas(a);
    } catch (e) {
      setError(e?.message || 'Failed to load walkthrough.');
    } finally {
      setLoading(false);
    }
  }, [walkthroughId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Default the selected area to the first one whenever areas are (re)loaded
  // and nothing is selected yet. Kept in its own effect so reloads don't
  // accidentally override a user-picked chip.
  useEffect(() => {
    if (!activeAreaId && areas.length > 0) {
      setActiveAreaId(areas[0].id);
    }
  }, [areas, activeAreaId]);

  /* -------------------------------------------------------------------------
   * Per-area refresh — photos + notes for the active area, signed URLs for
   * the photo paths, and rehydration of the autosave note draft.
   *
   * Note model: per spec the user types into a SINGLE notes field per area
   * (no "Save note" button). We persist this as the first pqv_note row for
   * the area; subsequent edits PATCH that row. Additional rows authored
   * elsewhere (e.g. import flows) still render in the read-only list below.
   * ---------------------------------------------------------------------- */
  const refreshActiveArea = useCallback(async (areaId) => {
    if (!areaId) {
      setPhotos([]);
      setPhotoUrls({});
      setNotes([]);
      setNoteDraft('');
      setPrimaryNoteId(null);
      noteLastSavedRef.current = '';
      setNoteSavingState('idle');
      return;
    }
    try {
      const [p, n] = await Promise.all([
        listAreaPhotos(areaId),
        listAreaNotes(areaId),
      ]);
      setPhotos(p);
      setNotes(n);
      const primary = n[0] || null;
      setPrimaryNoteId(primary?.id || null);
      setNoteDraft(primary?.body || '');
      noteLastSavedRef.current = primary?.body || '';
      setNoteSavingState('idle');
      const paths = p.map((row) => row.storage_path).filter(Boolean);
      const urlMap = paths.length ? await signedUrlsForPaths(paths) : {};
      setPhotoUrls(urlMap);
    } catch (e) {
      toast.error(e?.message || 'Failed to load area data.');
    }
  }, []);

  useEffect(() => {
    refreshActiveArea(activeAreaId);
  }, [activeAreaId, refreshActiveArea]);

  /* -------------------------------------------------------------------------
   * Add area
   * ---------------------------------------------------------------------- */
  const handleAddArea = async () => {
    const name = newAreaName.trim();
    if (!name) {
      toast.error(t('pqv.area_name_required', { defaultValue: 'Enter an area name.' }));
      return;
    }
    setAddingArea(true);
    try {
      const nextSeq =
        areas.length > 0
          ? Math.max(...areas.map((a) => Number(a.sequence) || 0)) + 1
          : 1;
      const created = await addArea({
        walkthroughId,
        name,
        sequence: nextSeq,
      });
      setAreas((prev) => [...prev, created]);
      setActiveAreaId(created.id);
      setNewAreaName('');
      toast.success(
        t('pqv.area_added_toast', {
          defaultValue: 'Area added — {{name}}',
          name: created.name,
        })
      );
    } catch (e) {
      toast.error(e?.message || 'Failed to add area.');
    } finally {
      setAddingArea(false);
    }
  };

  /* -------------------------------------------------------------------------
   * Photo upload
   * ---------------------------------------------------------------------- */
  const handlePhotoSelected = async (event) => {
    const files = Array.from(event?.target?.files || []);
    // Reset input so picking the same file(s) again re-fires onChange.
    if (event?.target) event.target.value = '';
    if (files.length === 0 || !activeAreaId) return;
    setUploading(true);
    let uploaded = 0;
    let failed = 0;
    try {
      let nextSeq =
        photos.length > 0
          ? Math.max(...photos.map((p) => Number(p.sequence) || 0)) + 1
          : 1;
      // Sequential on purpose: keeps sequence numbers deterministic and avoids
      // hammering storage from a jobsite connection with a 15-photo batch.
      for (const file of files) {
        try {
          await uploadAreaPhoto({
            file,
            areaId: activeAreaId,
            walkthroughId,
            sequence: nextSeq,
          });
          nextSeq += 1;
          uploaded += 1;
        } catch (e) {
          failed += 1;
        }
      }
      if (uploaded > 0) {
        toast.success(
          uploaded === 1
            ? t('pqv.photo_uploaded_toast', { defaultValue: 'Photo uploaded' })
            : t('pqv.photos_uploaded_toast', {
                defaultValue: '{{count}} photos uploaded',
                count: uploaded,
              })
        );
      }
      if (failed > 0) {
        toast.error(
          t('pqv.photos_failed_toast', {
            defaultValue: '{{count}} photo(s) failed to upload',
            count: failed,
          })
        );
      }
      await refreshActiveArea(activeAreaId);
    } catch (e) {
      toast.error(e?.message || 'Photo upload failed.');
    } finally {
      setUploading(false);
    }
  };

  /* -------------------------------------------------------------------------
   * Autosave note — persists `noteDraft` to the primary pqv_note row of the
   * active area. INSERTs on first non-empty save, then PATCHes the same row
   * on subsequent edits. Called from the debounce timer and on blur.
   *
   * Empty body is skipped (we don't create empty rows). If the user clears
   * an already-saved note we leave the existing row alone — explicit delete
   * goes through the SHIT button on the rendered note list below.
   * ---------------------------------------------------------------------- */
  const flushNoteSave = useCallback(
    async (areaId, draftBody) => {
      const body = (draftBody || '').trim();
      if (!areaId) return;
      if (body === noteLastSavedRef.current) return; // no-op
      if (!body && !primaryNoteId) return; // nothing to insert
      setNoteSavingState('saving');
      try {
        if (primaryNoteId) {
          const updated = await updateAreaNoteBody(primaryNoteId, body);
          noteLastSavedRef.current = body;
          setNotes((prev) =>
            prev.map((n) => (n.id === updated.id ? updated : n))
          );
        } else {
          const created = await addAreaNote({
            areaId,
            walkthroughId,
            body,
            source: 'typed',
          });
          setPrimaryNoteId(created.id);
          noteLastSavedRef.current = body;
          setNotes((prev) => [created, ...prev]);
        }
        setNoteSavingState('saved');
      } catch (e) {
        setNoteSavingState('error');
        toast.error(e?.message || 'Failed to save note.');
      }
    },
    [primaryNoteId, walkthroughId]
  );

  // Debounce — flush ~1s after the user stops typing. Cancels any pending
  // timer when the textarea changes again.
  const handleNoteChange = (value) => {
    setNoteDraft(value);
    setNoteSavingState('idle');
    if (noteDebounceRef.current) {
      clearTimeout(noteDebounceRef.current);
    }
    const areaId = activeAreaId;
    noteDebounceRef.current = setTimeout(() => {
      flushNoteSave(areaId, value);
    }, 1000);
  };

  const handleNoteBlur = () => {
    if (noteDebounceRef.current) {
      clearTimeout(noteDebounceRef.current);
      noteDebounceRef.current = null;
    }
    flushNoteSave(activeAreaId, noteDraft);
  };

  // Flush any pending debounced note on unmount or area change.
  useEffect(() => {
    return () => {
      if (noteDebounceRef.current) {
        clearTimeout(noteDebounceRef.current);
        noteDebounceRef.current = null;
      }
    };
  }, [activeAreaId]);

  // Lightbox keyboard handling — Esc closes the overlay. We bind globally
  // only while the lightbox is open to avoid leaking handlers.
  useEffect(() => {
    if (!lightboxPhoto) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxPhoto(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxPhoto]);

  // If the photo currently in the lightbox gets deleted, dismiss the overlay
  // so we never render a stale signed-url image.
  useEffect(() => {
    if (!lightboxPhoto) return;
    const stillPresent = photos.some((p) => p.id === lightboxPhoto.id);
    if (!stillPresent) setLightboxPhoto(null);
  }, [photos, lightboxPhoto]);

  /* -------------------------------------------------------------------------
   * Delete (SHIT) — area / photo / note. Two-step: open a confirm dialog,
   * then commit on confirm. The dialog is shared across all three kinds.
   * ---------------------------------------------------------------------- */
  const openConfirmDelete = (kind, row) => {
    setConfirmDelete({ open: true, target: { kind, row }, busy: false });
  };

  const closeConfirmDelete = () =>
    setConfirmDelete({ open: false, target: null, busy: false });

  const commitDelete = async () => {
    const target = confirmDelete.target;
    if (!target) return;
    setConfirmDelete((s) => ({ ...s, busy: true }));
    try {
      if (target.kind === 'note') {
        await deleteAreaNote(target.row.id);
        setNotes((prev) => prev.filter((n) => n.id !== target.row.id));
        if (primaryNoteId === target.row.id) {
          setPrimaryNoteId(null);
          setNoteDraft('');
          noteLastSavedRef.current = '';
        }
      } else if (target.kind === 'photo') {
        await deleteAreaPhoto(target.row);
        setPhotos((prev) => prev.filter((p) => p.id !== target.row.id));
        setPhotoUrls((prev) => {
          const next = { ...prev };
          delete next[target.row.storage_path];
          return next;
        });
      } else if (target.kind === 'area') {
        await deleteArea(target.row.id);
        const remaining = areas.filter((a) => a.id !== target.row.id);
        setAreas(remaining);
        if (activeAreaId === target.row.id) {
          // Pick the next available area (or clear selection).
          setActiveAreaId(remaining[0]?.id || null);
        }
      }
      toast.success(t('pqv.deleted_toast', { defaultValue: 'Deleted' }));
      closeConfirmDelete();
    } catch (e) {
      toast.error(e?.message || 'Delete failed.');
      setConfirmDelete((s) => ({ ...s, busy: false }));
    }
  };

  /* -------------------------------------------------------------------------
   * Done / Save → set status='complete' and bounce back to Estimates.
   * (Walkthroughs are part 1 of an estimate, so the natural return path is
   * the Estimates module rather than Active Jobs.)
   * ---------------------------------------------------------------------- */
  // Return path: land INSIDE the estimate this walkthrough belongs to, not
  // on the list — the Estimates page reopens the ?id it is handed.
  const estimatesReturnPath = walkthrough?.estimate_id
    ? `/dashboard/estimates?id=${walkthrough.estimate_id}`
    : '/dashboard/estimates';

  const handleDone = async () => {
    setCompleting(true);
    try {
      await completeWalkthrough(walkthroughId);
      toast.success(
        t('pqv.completed_toast', { defaultValue: 'Walkthrough saved' })
      );
      navigate(estimatesReturnPath);
    } catch (e) {
      toast.error(e?.message || 'Failed to complete walkthrough.');
    } finally {
      setCompleting(false);
    }
  };

  const activeArea = useMemo(
    () => areas.find((a) => a.id === activeAreaId) || null,
    [areas, activeAreaId]
  );

  /* -------------------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------------- */
  return (
    <div className="space-y-4" data-testid="pqv-capture-page">
      <PageHeader
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <PQShield size={16} />
            {t('pqv.eyebrow', { defaultValue: 'PQ INTELLIGENCE' })}
          </span>
        }
        title={t('pqv.capture_title', { defaultValue: 'PQ Vision' })}
        subtitle={t('pqv.capture_subtitle', {
          defaultValue:
            'Capture photos and notes per area. Done saves and returns to the estimate.',
        })}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(estimatesReturnPath)}
              className="text-muted-foreground hover:text-foreground uppercase tracking-[0.16em] text-xs"
              data-testid="pqv-back-button"
            >
              <ArrowLeft size={14} className="mr-2" />
              {t('pqv.back_to_estimates', { defaultValue: 'Back to estimates' })}
            </Button>
            <Button
              size="sm"
              onClick={handleDone}
              disabled={completing}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="pqv-done-button"
            >
              {completing ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <CheckCircle2 size={14} className="mr-2" />
              )}
              {t('pqv.done_button', { defaultValue: 'Done' })}
            </Button>
          </div>
        }
      />

      {error ? (
        <Alert
          className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="pqv-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {loading ? (
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-4 space-y-3">
            <Skeleton className="h-8 w-2/3 bg-[hsl(var(--muted))]" />
            <Skeleton className="h-32 w-full bg-[hsl(var(--muted))]" />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Area chips + add-area control */}
          <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
                <MapPin size={14} />
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {t('pqv.areas', { defaultValue: 'Areas' })}
                </div>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="pqv-area-chips">
                {areas.map((a) => (
                  <div
                    key={a.id}
                    className={cn(
                      'inline-flex items-center rounded-full border text-xs uppercase tracking-[0.16em] transition-colors',
                      a.id === activeAreaId
                        ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                        : 'border-border text-muted-foreground hover:border-[hsl(var(--primary))]/40 hover:text-foreground'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveAreaId(a.id)}
                      className="px-3 py-1.5 rounded-l-full"
                      data-testid={`pqv-area-chip-${a.id}`}
                    >
                      {a.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => openConfirmDelete('area', a)}
                      className="inline-flex items-center justify-center size-7 border-l border-current/15 text-muted-foreground/60 hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/5 rounded-r-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--destructive))]/40"
                      aria-label={t('pqv.shit_delete_area', {
                        defaultValue: 'Delete area',
                      })}
                      title={t('pqv.shit_delete_area', {
                        defaultValue: 'Delete area',
                      })}
                      data-testid={`pqv-area-shit-${a.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {areas.length === 0 ? (
                  <span className="text-xs text-muted-foreground italic">
                    {t('pqv.no_areas_yet', {
                      defaultValue: 'No areas yet — add the first one below.',
                    })}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-border">
                <Input
                  value={newAreaName}
                  onChange={(e) => setNewAreaName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !addingArea) {
                      e.preventDefault();
                      handleAddArea();
                    }
                  }}
                  placeholder={t('pqv.new_area_placeholder', {
                    defaultValue: 'e.g. Kitchen, Master bath, Exterior front',
                  })}
                  className="bg-[hsl(var(--input))] border-border text-foreground"
                  data-testid="pqv-new-area-input"
                />
                <Button
                  size="sm"
                  onClick={handleAddArea}
                  disabled={addingArea || !newAreaName.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                  data-testid="pqv-add-area-button"
                >
                  {addingArea ? (
                    <Loader2 size={14} className="mr-2 animate-spin" />
                  ) : (
                    <Plus size={14} className="mr-2" />
                  )}
                  {t('pqv.add_area_button', { defaultValue: 'Add area' })}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Capture panel — gated until an area is selected */}
          {activeArea ? (
            <>
              {/* Photos */}
              <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
                      <Camera size={14} />
                      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        {t('pqv.photos_for', {
                          defaultValue: 'Photos — {{name}}',
                          name: activeArea.name,
                        })}
                      </div>
                      <Badge
                        variant="outline"
                        className="ml-1 border-border text-muted-foreground text-[10px] uppercase tracking-[0.16em]"
                      >
                        {photos.length}
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      type="button"
                      onClick={() => {
                        // Reset value FIRST so onChange fires even if the
                        // same file is picked twice in a row, then trigger
                        // the picker synchronously within the user gesture
                        // (required for iOS Safari / WebView shells).
                        const input = fileInputRef.current;
                        if (!input) return;
                        input.value = '';
                        input.click();
                      }}
                      disabled={uploading}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                      data-testid="pqv-photo-button"
                    >
                      {uploading ? (
                        <Loader2 size={14} className="mr-2 animate-spin" />
                      ) : (
                        <Camera size={14} className="mr-2" />
                      )}
                      {t('pqv.take_photo', { defaultValue: 'Take / pick photo' })}
                    </Button>
                    {/* No `capture` attribute on purpose: forcing the dedicated
                        camera hides the gallery (can't upload photos already
                        taken) AND makes Android evict/reload the app while the
                        camera is open — the same failure the receipt scanner
                        hit. The OS picker offers camera + gallery + files, and
                        `multiple` lets a whole walkthrough batch come in at
                        once. */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoSelected}
                      // NOTE: do NOT use `display: none` (Tailwind's `hidden`)
                      // here — iOS Safari and several WebView shells will
                      // silently swallow a programmatic .click() on a
                      // display:none file input. Visually-hide it with
                      // positioning + opacity so the picker still opens.
                      style={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        opacity: 0,
                        pointerEvents: 'none',
                        left: -9999,
                      }}
                      tabIndex={-1}
                      aria-hidden="true"
                      data-testid="pqv-photo-input"
                    />
                  </div>
                  {photos.length === 0 ? (
                    <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                      {t('pqv.no_photos_yet', {
                        defaultValue:
                          'No photos yet. Tap "Take / pick photo" to capture this area.',
                      })}
                    </div>
                  ) : (
                    <div
                      className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2"
                      data-testid="pqv-photo-grid"
                    >
                      {photos.map((p) => {
                        const url = photoUrls[p.storage_path];
                        return (
                          <div
                            key={p.id}
                            className="relative group aspect-square rounded-md overflow-hidden border border-border bg-[hsl(var(--muted))]"
                            data-testid={`pqv-photo-thumb-${p.id}`}
                          >
                            <button
                              type="button"
                              onClick={() => setLightboxPhoto(p)}
                              disabled={!url}
                              className="block w-full h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/60"
                              aria-label={t('pqv.open_photo', {
                                defaultValue: 'Open photo full screen',
                              })}
                              data-testid={`pqv-photo-open-${p.id}`}
                            >
                              {url ? (
                                <img
                                  src={url}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                  <Camera size={16} />
                                </div>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                // Don't bubble up to the thumbnail's open
                                // button — the trash should ONLY delete, not
                                // simultaneously open the lightbox.
                                e.stopPropagation();
                                openConfirmDelete('photo', p);
                              }}
                              className="absolute top-1 right-1 inline-flex items-center justify-center size-6 rounded-md bg-black/55 text-white hover:bg-[hsl(var(--destructive))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--destructive))]/60 backdrop-blur-sm shadow-sm"
                              aria-label={t('pqv.shit_delete_photo', {
                                defaultValue: 'Delete photo',
                              })}
                              title={t('pqv.shit_delete_photo', {
                                defaultValue: 'Delete photo',
                              })}
                              data-testid={`pqv-photo-shit-${p.id}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notes — autosave on debounce (~1s) and on blur. The textarea
                  is the only input; "Save note" no longer exists. The status
                  indicator on the right reflects pending / saved / error. */}
              <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
                      <StickyNote size={14} />
                      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        {t('pqv.notes_for', {
                          defaultValue: 'Notes — {{name}}',
                          name: activeArea.name,
                        })}
                      </div>
                    </div>
                    <div
                      className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground inline-flex items-center gap-1"
                      data-testid="pqv-note-autosave-state"
                    >
                      {noteSavingState === 'saving' ? (
                        <>
                          <Loader2 size={10} className="animate-spin" />
                          {t('pqv.note_saving', { defaultValue: 'Saving…' })}
                        </>
                      ) : noteSavingState === 'saved' ? (
                        <>
                          <CheckCircle2
                            size={10}
                            className="text-[hsl(var(--success))]"
                          />
                          {t('pqv.note_saved', { defaultValue: 'Saved' })}
                        </>
                      ) : noteSavingState === 'error' ? (
                        <>
                          <AlertCircle
                            size={10}
                            className="text-[hsl(var(--destructive))]"
                          />
                          {t('pqv.note_save_failed', {
                            defaultValue: 'Save failed',
                          })}
                        </>
                      ) : (
                        <span className="text-muted-foreground/70">
                          {t('pqv.note_autosaves', {
                            defaultValue: 'Autosaves',
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  <Textarea
                    value={noteDraft}
                    onChange={(e) => handleNoteChange(e.target.value)}
                    onBlur={handleNoteBlur}
                    placeholder={t('pqv.note_placeholder', {
                      defaultValue:
                        'What did you observe in this area? Conditions, materials, concerns...',
                    })}
                    className="bg-[hsl(var(--input))] border-border text-foreground min-h-[100px]"
                    data-testid="pqv-note-textarea"
                  />
                  {notes.length > 0 ? (
                    <ul
                      className="space-y-2 pt-2 border-t border-border"
                      data-testid="pqv-notes-list"
                    >
                      {notes.map((n) => (
                        <li
                          key={n.id}
                          className="rounded-md border border-border bg-[hsl(var(--muted))]/30 px-3 py-2 text-sm text-foreground flex items-start justify-between gap-2"
                          data-testid={`pqv-note-${n.id}`}
                        >
                          <span className="flex-1 break-words">{n.body}</span>
                          <button
                            type="button"
                            onClick={() => openConfirmDelete('note', n)}
                            className="shrink-0 inline-flex items-center gap-1 rounded-md border border-[hsl(var(--destructive))]/30 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 uppercase tracking-[0.16em] text-[10px] font-semibold px-2 py-0.5"
                            aria-label={t('pqv.shit_delete_note', {
                              defaultValue: 'Delete note',
                            })}
                            data-testid={`pqv-note-shit-${n.id}`}
                          >
                            <Trash2 size={10} />
                            SHIT
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                {t('pqv.select_area_help', {
                  defaultValue:
                    'Add an area above to start capturing photos and notes.',
                })}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* PQ Vision AI — per-photo findings + confirm/edit/reject review. */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <FindingsReview
            walkthroughId={walkthroughId}
            estimateId={walkthrough?.estimate_id || null}
            areas={areas}
          />
        </CardContent>
      </Card>

      {walkthrough?.status === 'complete' ? (
        <Alert
          className="border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-foreground"
          data-testid="pqv-already-complete"
        >
          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
          <AlertDescription className="text-sm">
            {t('pqv.already_complete', {
              defaultValue:
                'This walkthrough is already marked complete. Edits still save to the same record.',
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Lightbox — full-screen photo viewer. Backdrop click and the close
          button both dismiss. We reuse the same signed URL the thumbnail
          minted so we don't burn extra storage calls. */}
      {lightboxPhoto ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('pqv.lightbox_label', { defaultValue: 'Photo viewer' })}
          onClick={() => setLightboxPhoto(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          data-testid="pqv-photo-lightbox"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxPhoto(null);
            }}
            className="absolute top-4 right-4 inline-flex items-center justify-center size-10 rounded-full bg-white/10 text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            aria-label={t('pqv.lightbox_close', { defaultValue: 'Close' })}
            data-testid="pqv-photo-lightbox-close"
          >
            <X size={18} />
          </button>
          {photoUrls[lightboxPhoto.storage_path] ? (
            <img
              src={photoUrls[lightboxPhoto.storage_path]}
              alt=""
              // Stop the backdrop dismiss on clicks that land on the image
              // itself — feels weird if tapping the photo closes the viewer.
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
              data-testid="pqv-photo-lightbox-image"
            />
          ) : (
            <div className="text-white/70 text-sm">
              {t('pqv.lightbox_unavailable', {
                defaultValue: 'Photo unavailable.',
              })}
            </div>
          )}
        </div>
      ) : null}

      {/* Delete (SHIT) confirmation — shared across areas, photos, and notes.
          Photo deletes also remove the file from the pqv-photos bucket via
          pqvData.deleteAreaPhoto. */}
      <AlertDialog
        open={confirmDelete.open}
        onOpenChange={(o) => {
          if (!o && !confirmDelete.busy) closeConfirmDelete();
        }}
      >
        <AlertDialogContent
          className="bg-[hsl(var(--card))] border-border"
          data-testid="pqv-shit-confirm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {t('pqv.shit_confirm_title', { defaultValue: 'You sure?' })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {confirmDelete.target?.kind === 'area'
                ? t('pqv.shit_confirm_area', {
                    defaultValue:
                      'This deletes the area and all of its photos and notes. Cannot be undone.',
                  })
                : confirmDelete.target?.kind === 'photo'
                ? t('pqv.shit_confirm_photo', {
                    defaultValue:
                      'This deletes the photo and removes the file from storage. Cannot be undone.',
                  })
                : t('pqv.shit_confirm_note', {
                    defaultValue: 'This deletes the note. Cannot be undone.',
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-border text-foreground hover:bg-[hsl(var(--muted))]"
              disabled={confirmDelete.busy}
              data-testid="pqv-shit-cancel"
            >
              {t('actions.cancel', { defaultValue: 'Cancel' })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={commitDelete}
              disabled={confirmDelete.busy}
              className="bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]/90"
              data-testid="pqv-shit-confirm-action"
            >
              {confirmDelete.busy ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <Trash2 size={14} className="mr-2" />
              )}
              {t('pqv.shit_confirm_action', { defaultValue: 'Delete' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
